import {
  mergeAttributes,
  Node,
  ReactNodeViewRenderer,
  type NodeViewProps,
} from "@tiptap/react";
import { useCallback } from "react";
import {
  DID_ATTR,
  DID_NODE_NAME,
  DID_SELECTION_KEY,
  isDidReference,
} from "./constants";
import { DynamicChipNodeView } from "./dynamic-chip-node";
import { useDynamicNodeContext } from "./dynamic-node-context";
import { consumeEditorOpen } from "./slash-command/data-key-registry";

export const DidNodeView: React.FC<NodeViewProps> = ({
  node,
  updateAttributes,
  deleteNode,
  editor,
  getPos,
}) => {
  const did = (node.attrs[DID_ATTR] as string) ?? "";
  const {
    resolveContext,
    resolveDocumentReferenceOwner,
    contextKeys,
    frozen,
    loading,
  } = useDynamicNodeContext();
  const resolution =
    did && did !== DID_SELECTION_KEY ? resolveContext(did) : null;
  const selectNode = useCallback(() => {
    const position = getPos();
    if (typeof position === "number") {
      editor.chain().focus().setNodeSelection(position).run();
    }
  }, [editor, getPos]);

  return (
    <DynamicChipNodeView
      node={node}
      updateAttributes={updateAttributes}
      deleteNode={deleteNode}
      onSelect={selectNode}
      config={{
        attr: DID_ATTR,
        resolution,
        owner:
          resolution?.value && !resolution.isUnresolved
            ? resolveDocumentReferenceOwner?.(did, resolution.value)
            : undefined,
        getItems: () => contextKeys.filter((item) => isDidReference(item.key)),
        consumePendingOpen: () => consumeEditorOpen("did"),
        loading,
        frozen,
        interactive: editor.isEditable,
        headerLabel: "Document reference",
        emptyLabel: "Select document",
        searchPlaceholder: "Search documents…",
        noMatchLabel: "No matching documents",
        renderResolvedAsPlainText: true,
        showKeys: false,
        showInlineOwnerLink: true,
        isEmptyValue: (value) => value === DID_SELECTION_KEY,
        info: {
          note: (
            <>
              All device and organization documents are available. Missing
              identifier parts are shown as {"<MISSING>"}.
            </>
          ),
        },
      }}
    />
  );
};

declare module "@tiptap/react" {
  interface Commands<ReturnType> {
    did: {
      insertDid: (did: string) => ReturnType;
    };
  }
}

export const DidNode = Node.create({
  name: DID_NODE_NAME,
  group: "inline",
  inline: true,
  atom: true,
  selectable: true,

  addAttributes() {
    return {
      [DID_ATTR]: {
        default: "",
        parseHTML: (element) => element.getAttribute(`data-${DID_ATTR}`) ?? "",
        renderHTML: (attributes) => ({
          [`data-${DID_ATTR}`]: attributes[DID_ATTR],
        }),
      },
    };
  },

  parseHTML() {
    return [{ tag: `span[data-type="${DID_NODE_NAME}"]` }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      "span",
      mergeAttributes({ "data-type": DID_NODE_NAME }, HTMLAttributes),
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(DidNodeView);
  },

  addCommands() {
    return {
      insertDid:
        (did: string) =>
        ({ commands }) =>
          commands.insertContent({
            type: this.name,
            attrs: { [DID_ATTR]: did },
          }),
    };
  },
});

export default DidNode;
